import { ControlAuthorities } from '@src/types/ControlModes';
import { generateDataPoint } from '@src/utils/generateTopic';
import { StatusType, StatusTypeKey } from '../types';

interface StatusTypeConfig {
  authority: ControlAuthorities;
  getBaseMessage: (alias: (key: string) => string) => string;
  getEnabledMessage: (alias: (key: string) => string) => string;
  getSwitchMessage: (alias: (key: string) => string) => string;
}

const STATUS_TYPE_CONFIG: Record<
  StatusTypeKey.SCADA | StatusTypeKey.HMI,
  StatusTypeConfig
> = {
  [StatusTypeKey.SCADA]: {
    authority: ControlAuthorities.scada,
    getBaseMessage: (alias) =>
      alias('Mode status that is set by SCADA commands.'),
    getEnabledMessage: (alias) =>
      alias(
        'The authority is set to SCADA. The status can be enabled/disabled only from the customer SCADA system.',
      ),
    getSwitchMessage: (alias) =>
      alias(
        'Switch to SCADA authority to enable/disable modes from the customer SCADA system.',
      ),
  },
  [StatusTypeKey.HMI]: {
    authority: ControlAuthorities.hmi,
    getBaseMessage: (alias) =>
      alias('Mode status that is set by the HMI user.'),
    getEnabledMessage: (alias) =>
      alias(
        'The authority is set to HMI. The status can be enabled/disabled from this page.',
      ),
    getSwitchMessage: (alias) =>
      alias('Switch to HMI authority to enable/disable modes from this page.'),
  },
};

const getAuthorityDisplayName = (authority: ControlAuthorities): string => {
  return ControlAuthorities[authority]?.toUpperCase() ?? 'UNKNOWN';
};

const buildAuthorityMessage = (
  config: StatusTypeConfig,
  controlAuthority: ControlAuthorities | undefined | null,
  alias: (key: string) => string,
): string => {
  const baseMsg = config.getBaseMessage(alias);

  if (controlAuthority === config.authority) {
    return `${baseMsg} ${config.getEnabledMessage(alias)}`;
  }

  if (controlAuthority != null) {
    const authorityName = getAuthorityDisplayName(controlAuthority);
    return `${baseMsg} ${alias('The authority is set to')} ${authorityName}. ${config.getSwitchMessage(alias)}`;
  }

  return `${baseMsg} ${alias('The authority is not set')}`;
};

export const generateStatusTooltip = (
  statusType: StatusTypeKey,
  controlAuthority: ControlAuthorities | undefined | null,
  alias: (key: string) => string,
): string => {
  const config =
    STATUS_TYPE_CONFIG[statusType as StatusTypeKey.SCADA | StatusTypeKey.HMI];

  if (config) {
    return buildAuthorityMessage(config, controlAuthority, alias);
  }

  if (controlAuthority != null) {
    const authorityName = getAuthorityDisplayName(controlAuthority);
    return `${alias('Current run status of the control mode. The authority is set to')} ${authorityName} ${alias('and the status is set by')} ${authorityName} ${alias('Status')}.`;
  }

  return alias(
    'Current run status of the control mode. The authority is not set.',
  );
};

export const generateStatusTypes = (
  modeName: string,
  controlAuthority: ControlAuthorities | undefined | null,
  alias: (key: string) => string,
  siteId: string,
): StatusType[] => {
  const types: Array<{
    key: StatusTypeKey;
    pointName: string;
    displayName: string;
  }> = [
    {
      key: StatusTypeKey.SCADA,
      pointName: `Disable_${modeName}_SCADA`,
      displayName: `${modeName} SCADA`,
    },
    {
      key: StatusTypeKey.HMI,
      pointName: `Disable_${modeName}_Fractal`,
      displayName: `${modeName} HMI`,
    },
    {
      key: StatusTypeKey.ACTIVE,
      pointName: `Disable_${modeName}`,
      displayName: `${modeName} ${alias('Disable')}`,
    },
  ];

  return types.map((type) => ({
    ...type,
    tooltipMessage: generateStatusTooltip(type.key, controlAuthority, alias),
    topic: generateDataPoint(
      {
        deviceType: 'site',
        controllerId: siteId,
        pointName: type.pointName,
        dataType: 'integer',
      },
      siteId,
    ).fullTopic,
  }));
};

export const extractStatusValues = (
  statusTypes: StatusType[],
  mqttValues: Record<string, { value: unknown } | null>,
): {
  scadaStatus: number | undefined;
  hmiStatus: number | undefined;
  activeStatus: number | undefined;
} => {
  const [scadaType, hmiType, activeType] = statusTypes;

  return {
    scadaStatus: scadaType
      ? (mqttValues[scadaType.topic]?.value as number | undefined)
      : undefined,
    hmiStatus: hmiType
      ? (mqttValues[hmiType.topic]?.value as number | undefined)
      : undefined,
    activeStatus: activeType
      ? (mqttValues[activeType.topic]?.value as number | undefined)
      : undefined,
  };
};
